import * as React from "react";

import { IconClose, IconSearch } from "@common/components/Atoms/Icons";

export interface SearchEntryProps {
    dark?: boolean;
    getRemoteOptions?: (value: string) => Promise<any>;
    isTypeAhead?: boolean;
    keyword?: string;
    label?: string;
    name?: string;
    onEnterKey?: (e: any) => void;
    onSubmit?: (value: any) => void;
    onTypedValueChanged?: (value: any) => void;
    options?: any[];
    overlay?: boolean;
    selectedValue?: object;
    submitOnEnter?: boolean;
    tabIndex?: number;
    value?: any;
    dataCy?: string;
    showIcon?: boolean;
}

interface SearchEntryState {
    char_max: number;
    current_item: any;
    current_typed_value: string;
    has_blurred: boolean;
    is_focused: boolean;
    is_initial_state: boolean;
    is_open: boolean;
    is_valid: boolean;
    options: any[];
    selectedValue: any;
    show_message: boolean;
    validation_message: string;
}

export class SearchEntry extends React.Component<
    SearchEntryProps,
    SearchEntryState
> {
    static defaultProps = {
        name: "searchEntry",
        options: [],
        isTypeAhead: false,
        submitOnEnter: false,
    };

    constructor(props: SearchEntryProps) {
        super(props);

        const options: any[] = props.options || [];
        const selopt = options.find(x => x.value === props.selectedValue);

        this.state = {
            char_max: 0,
            current_item: !!selopt ? selopt : "",
            current_typed_value: this.props.keyword || "",
            has_blurred: false,
            is_focused: false,
            is_initial_state: true,
            is_open: false,
            is_valid: true,
            options: props.options || [],
            selectedValue: "",
            show_message: false,
            validation_message: "",
        };

        this.typedValueChanged = this.typedValueChanged.bind(this);
        this.valueChanged = this.valueChanged.bind(this);

        this.toggleDrop = this.toggleDrop.bind(this);

        this.setWrapperRef = this.setWrapperRef.bind(this);
        this.handleClickOutside = this.handleClickOutside.bind(this);

        this.clearValue = this.clearValue.bind(this);
        this.onKeyPress = this.onKeyPress.bind(this);
    }

    private wrapperRef: any;

    componentDidMount() {
        document.addEventListener("mousedown", this.handleClickOutside);
    }

    componentWillUnmount() {
        document.removeEventListener("mousedown", this.handleClickOutside);
    }

    componentWillReceiveProps(newProps) {
        if (newProps.keyword !== this.props.keyword) {
            this.setState({ current_typed_value: newProps.keyword });
        }
    }

    /*** Set the wrapper ref*/
    setWrapperRef(node) {
        this.wrapperRef = node;
    }

    /*** Alert if clicked on outside of element*/
    handleClickOutside(event) {
        if (this.wrapperRef && !this.wrapperRef.contains(event.target)) {
            this.setState(prevState => ({
                is_open: false,
                current_typed_value: prevState.current_typed_value,
            }));
        }
    }

    toggleDrop() {
        this.setState({ is_open: !this.state.is_open });
    }

    valueChanged(item) {
        this.setState(
            {
                current_item: item,
                is_initial_state: false,
            },
            () => {
                if (this.state.has_blurred) this.validate();
            }
        );
    }

    validate = () => {
        // noop = called from valueChanged above, but only if has_burred is true.  However, has_blurred is never changed from its default of false.
        // not sure of the intention here, and whether or not we need to remove.
    };

    onKeyPress(e) {
        if (e.key === "Enter" && !!this.props.onEnterKey) {
            e.preventDefault();
            e.persist();
            this.props.onEnterKey(e);
        }
    }

    typedValueChanged(e) {
        this.setState({ current_typed_value: e.target.value }, () => {
            if (this.props.isTypeAhead) {
                if (this.state.current_typed_value.length >= 3) {
                    if (this.props.getRemoteOptions) {
                        this.props
                            .getRemoteOptions(this.state.current_typed_value)
                            .then((options: any[]) => {
                                const o = options.slice(0, 25);
                                this.setState({ options: o, is_open: true });
                            });
                    }
                } else {
                    this.setState({ is_open: false });
                }
            }
            if (this.props.onTypedValueChanged) {
                this.props.onTypedValueChanged(this.state.current_typed_value);
            }
        });
    }

    selectItem(item) {
        if (item.value !== this.state.current_item.value) {
            this.valueChanged(item);
        }
        this.setState(
            {
                current_item: item,
                is_open: false,
                current_typed_value: item.label,
            },
            () => {
                this.onSubmit(item);
            }
        );
    }

    onSubmit(item) {
        if (this.props.onSubmit) this.props.onSubmit(item);
    }

    onFormSubmit(event, item) {
        event.preventDefault();
        if (this.state.options && this.state.options.length) {
            this.selectItem(this.state.options[0]);
        } else {
            this.onSubmit(item);
        }
    }

    clearValue() {
        this.setState({
            current_item: "",
            current_typed_value: "",
            is_open: false,
            is_focused: true,
        });
    }

    render() {
        const {
            label,
            showIcon = true,
            tabIndex,
            name,
            submitOnEnter,
            dark,
            overlay,
            dataCy,
        } = this.props;

        const {
            is_open,
            current_typed_value,
            options,
            selectedValue,
        } = this.state;

        let keywordInput: any | null = null;
        const classes = ["c-search"];
        if (dark) classes.push("c-search--dark");
        if (overlay) classes.push("c-search--overlay");

        const searchEntryContent = (
            <div
                className={classes.join(" ") + " " + (is_open ? "is-open" : "")}
            >
                <label className="c-search__label">{label}</label>
                <div className="c-search__control" ref={this.setWrapperRef}>
                    <input
                        className="c-search__input"
                        type="text"
                        placeholder="search"
                        data-cy={dataCy}
                        value={current_typed_value}
                        onChange={this.typedValueChanged}
                        onKeyPress={this.onKeyPress}
                        tabIndex={tabIndex}
                        ref={input => {
                            keywordInput = input;
                        }}
                    />
                    {showIcon && (
                        <button
                            className="c-search__submit"
                            type="button"
                            onClick={() => this.onSubmit(keywordInput.value)}
                            tabIndex={-1}
                        >
                            <IconSearch />
                        </button>
                    )}
                    <div className="c-search__suggest c-search__suggest--hacked">
                        <div className="c-control__wrapper" id={"id_" + name}>
                            <div className="c-control__options">
                                {options && options.length === 0 && (
                                    <div
                                        className="c-control__option"
                                        onClick={() => this.clearValue()}
                                    >
                                        sorry, no matches <IconClose />
                                    </div>
                                )}
                                {options.length > 0 &&
                                    options.map((opt, index) => (
                                        <div
                                            key={"key" + index}
                                            className={
                                                "c-control__option " +
                                                (selectedValue === opt.value
                                                    ? "is-selected"
                                                    : "")
                                            }
                                            onClick={() => this.selectItem(opt)}
                                            data-cy={`location-suggest-options-${index + 1}`}
                                        >
                                            {opt.label}
                                        </div>
                                    ))}
                            </div>
                        </div>
                    </div>
                </div>
            </div>
        );

        return submitOnEnter ? (
            <form
                onSubmit={event => this.onFormSubmit(event, keywordInput.value)}
            >
                {searchEntryContent}
            </form>
        ) : (
            searchEntryContent
        );
    }
}

export * from "./locations";
export * from "./jobs";
export * from "./candidates";
